import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { LinkWithTitle } from '@/components/links';
import { AnchorWithTitle } from '@/components/links/anchor';

export const CUSTOM_SETLIST_DJ_TITLE = 'Custom-Setlist DJ';

export const Head = () => {
  return (
    <ComponentsHead
      title="Custom DJ Boat Party Add-On | Tampa Bay Boating Adventures"
      description="Upgrade your boat party with a pro DJ spinning your custom setlist. No playlists, just pure energy with live mixing tailored to your vibe."
      keywords="DJ, Music, Add-On, Boat Party"
    />
  );
};

const AddOnsCustomSetlistDj: FC = () => {
  return (
    <AddOnsTemplate
      title="Custom DJ Setlist Add-On for Tampa Boat Charters"
      subtitle={CUSTOM_SETLIST_DJ_TITLE}
      paragraphs={[
        <>
          When it comes to curating the perfect vibe for
          your{' '}
          <LinkWithTitle to="/services/4-hour-private-party-boat">
            day on the water
          </LinkWithTitle>
          , nothing compares to a live DJ mixing a custom
          set just for you. Our Custom-Setlist DJ is our
          most popular add-on—and with good reason. This
          isn’t your average playlist. This is a
          professionally mixed soundtrack, built
          specifically around your taste, your group, and
          your energy.
        </>,
        <>
          When you book this{' '}
          <LinkWithTitle to="/services/charter-add-ons">
            add-on
          </LinkWithTitle>
          , we’ll ask you a few questions about your music
          preferences. Whether you’re into modern house, 70s
          funk, throwback hip hop, deep chill, country
          classics, or a mashup of everything, your DJ will
          pre-build a custom mix that fits your vibe. On the
          day of your{' '}
          <LinkWithTitle to="/services/">
            private charter
          </LinkWithTitle>
          , they’ll come aboard with a full professional DJ
          deck and plug directly into our{' '}
          <LinkWithTitle to="/our-boat/">
            state-of-the-art sound system
          </LinkWithTitle>
          . No Bluetooth glitches. No repeat tracks. Just
          seamless transitions, expertly mixed beats, and an
          unforgettable soundtrack that evolves with your
          cruise.
        </>,
        <>
          Our DJs are five-star rated professionals,
          performing regularly at top nightclubs, lounges,
          and events across{' '}
          <AnchorWithTitle href="https://www.visittampabay.com/">
            Tampa
          </AnchorWithTitle>
          . They bring big energy, polished skill, and
          top-tier equipment to your private event on the
          water. Each DJ is experienced in reading a crowd
          and adjusting their performance to keep the energy
          just right—whether you're looking for an upbeat
          party or a more chill, beachy atmosphere.
        </>,
        'This is hands-down the top recommendation from our team and the owners of Tampa Bay Boating Adventures themselves. It’s the number one way to turn a fun day on the water into something truly spectacular. Whether you’re planning a birthday bash, a bachelorette party, or a just-because weekend with friends, this upgrade brings the magic. Each set is unique, tailored, and designed to make your charter a one-of-a-kind experience you won’t forget.',
      ]}
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${CUSTOM_SETLIST_DJ_TITLE} header`}
              src="../../images/add-ons/custom-setlist-dj/CustomDJ.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${CUSTOM_SETLIST_DJ_TITLE} slide 1`}
              src="../../images/add-ons/custom-setlist-dj/CustomDJ2.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${CUSTOM_SETLIST_DJ_TITLE} slide 2`}
              src="../../images/add-ons/custom-setlist-dj/CustomDJ3.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
    </AddOnsTemplate>
  );
};

export default AddOnsCustomSetlistDj;
